import { config } from "../config";
import { setCookie } from "../lib/cookies";
import type { UserData, WSMessage } from "../types";
import { GUEST } from "../types";

// WebSocket client for the legacy Tornado server.
//
// Most handlers use `{ tag, content }` where `content` is a JSON string:
// `{ …fields, requestId }`, and replies use `{ tag: "response:"+requestId,
// content }` (main.js sendActionAndWait). See `awaitLegacyResponse`.
//
// `sendAndWait` uses `{ tag, content, id }` instead — not wired for backend
// `pixel` / `reserve`; kept for experimentation.
//
// Authentication is implicit: the HttpOnly auth_token cookie is attached by
// the browser to the WS upgrade request — we never see the token in JS.

type Listener<T = unknown> = (msg: WSMessage<T>) => void;

interface Pending {
  resolve: (msg: WSMessage) => void;
  reject: (err: Error) => void;
  timer: number;
}

const PING_INTERVAL_MS = 30_000;
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30_000;

export class SocketClient {
  private socket: WebSocket | null = null;
  private pingTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private reconnectAttempts = 0;
  private reconnectDisabled = false;
  private isConnecting = false;
  private connectAcknowledged = false;
  private messageQueue: string[] = [];
  private pendingById = new Map<string, Pending>();
  private pendingLegacy = new Map<
    string,
    { resolve: (c: unknown) => void; reject: (e: Error) => void; timer: number }
  >();
  private listenersByTag = new Map<WSMessage["tag"], Set<Listener>>();
  private statusListeners = new Set<(connected: boolean) => void>();
  private handshakeWaiters: Array<(data: UserData) => void> = [];

  public userdata: UserData = { ...GUEST };

  connect(): Promise<UserData> {
    if (this.isConnecting && this.socket) {
      return new Promise((resolve) => {
        this.handshakeWaiters.push(resolve);
      });
    }
    if (
      this.socket &&
      this.socket.readyState === WebSocket.OPEN &&
      this.connectAcknowledged
    ) {
      return Promise.resolve(this.userdata);
    }

    this.isConnecting = true;
    this.connectAcknowledged = false;

    return new Promise<UserData>((resolve) => {
      this.handshakeWaiters.push(resolve);
      this.openSocket();
    });
  }

  close() {
    if (this.reconnectTimer != null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.pingTimer != null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.socket) {
      try {
        this.socket.close();
      } catch {
        /* ignore */
      }
      this.socket = null;
    }
    this.connectAcknowledged = false;
    this.isConnecting = false;
  }

  /** Reconnect after auth state changed (e.g. after Google sign-in). */
  reconnect(): Promise<UserData> {
    this.reconnectDisabled = false;
    this.close();
    return this.connect();
  }

  send(tag: string, content: unknown = "") {
    const msg = JSON.stringify({ tag, content });
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(msg);
    } else {
      this.messageQueue.push(msg);
    }
  }

  /** Send `{tag, content}` and wait for a matching response. */
  sendAndWait<T = unknown>(
    tag: string,
    content: unknown = "",
    timeoutMs = 8000,
  ): Promise<WSMessage<T>> {
    return new Promise((resolve, reject) => {
      const id = crypto.randomUUID();
      const payload = JSON.stringify({ tag, content, id });

      const timer = window.setTimeout(() => {
        this.pendingById.delete(id);
        reject(new Error(`WS request "${tag}" timed out`));
      }, timeoutMs);

      this.pendingById.set(id, {
        resolve: (m) => resolve(m as WSMessage<T>),
        reject,
        timer,
      });

      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(payload);
      } else {
        this.messageQueue.push(payload);
      }
    });
  }

  /**
   * Legacy Tornado handshake: reply tag is `response:${requestId}` (see
   * `sendActionAndWait` in `_legacy/static/js/main.js`).
   */
  awaitLegacyResponse<T = unknown>(
    wsTag: string,
    payload: Record<string, unknown>,
    timeoutMs = 20000,
  ): Promise<T> {
    const requestId = Math.random().toString(36).substring(2, 15);
    const body = JSON.stringify({ ...payload, requestId });
    return new Promise<T>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.pendingLegacy.delete(requestId);
        reject(new Error(`WebSocket "${wsTag}" timed out`));
      }, timeoutMs);
      this.pendingLegacy.set(requestId, {
        resolve: (c) => resolve(c as T),
        reject,
        timer,
      });
      this.send(wsTag, body);
    });
  }

  on<T = unknown>(tag: string, fn: Listener<T>): () => void {
    let set = this.listenersByTag.get(tag);
    if (!set) {
      set = new Set();
      this.listenersByTag.set(tag, set);
    }
    set.add(fn as Listener);
    return () => set?.delete(fn as Listener);
  }

  onStatusChange(fn: (connected: boolean) => void): () => void {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }

  private notifyStatus(connected: boolean) {
    this.statusListeners.forEach((fn) => fn(connected));
  }

  private openSocket() {
    let socket: WebSocket;
    try {
      socket = new WebSocket(config.wsUrl);
    } catch (err) {
      console.error("Failed to construct WebSocket", err);
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      this.reconnectAttempts = 0;
      // Drain any queued messages.
      while (this.messageQueue.length > 0) {
        const msg = this.messageQueue.shift()!;
        socket.send(msg);
      }
      // Start ping loop.
      this.pingTimer = window.setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ tag: "ping", content: "" }));
        }
      }, PING_INTERVAL_MS);
    };

    socket.onmessage = (ev) => {
      let parsed: WSMessage;
      try {
        parsed = JSON.parse(ev.data);
      } catch {
        return;
      }

      // Anonymous cart session: server sends a signed session_id when no cookie
      // exists yet (see WebSocketHandler._open_impl). Must persist before
      // reconnect (e.g. after Google sign-in) so guest cart merges into user cart.
      if (parsed.tag === "session") {
        const signed =
          typeof parsed.content === "string" ? parsed.content : "";
        if (signed) setCookie("session_id", signed);
      }

      // Handshake: backend sends `connected` with the user data.
      if (parsed.tag === "connected") {
        this.connectAcknowledged = true;
        this.isConnecting = false;
        const data = (parsed.content || {}) as UserData;
        this.userdata = { ...GUEST, ...data, initialized: true };
        this.notifyStatus(true);
        const waiters = this.handshakeWaiters.splice(0);
        waiters.forEach((fn) => fn(this.userdata));
      }

      if (parsed.tag === "banned") {
        this.reconnectDisabled = true;
        try {
          socket.close(4003, "banned");
        } catch {
          /* ignore */
        }
        return;
      }

      if (parsed.tag === "terminate") {
        try {
          socket.close();
        } catch {
          /* ignore */
        }
        return;
      }

      if (parsed.id && this.pendingById.has(parsed.id)) {
        const p = this.pendingById.get(parsed.id)!;
        this.pendingById.delete(parsed.id);
        clearTimeout(p.timer);
        p.resolve(parsed);
      }

      if (
        typeof parsed.tag === "string" &&
        parsed.tag.startsWith("response:") &&
        parsed.tag !== "response:"
      ) {
        const requestId = parsed.tag.slice("response:".length);
        const leg = this.pendingLegacy.get(requestId);
        if (leg) {
          clearTimeout(leg.timer);
          this.pendingLegacy.delete(requestId);
          leg.resolve(parsed.content);
        }
      }

      const set = this.listenersByTag.get(parsed.tag);
      if (set) set.forEach((fn) => fn(parsed));
    };

    socket.onclose = (ev) => {
      if (this.pingTimer != null) {
        clearInterval(this.pingTimer);
        this.pingTimer = null;
      }
      this.connectAcknowledged = false;
      this.isConnecting = false;
      this.notifyStatus(false);
      // Reject any pending requests so callers don't hang forever.
      this.pendingById.forEach((p) => {
        clearTimeout(p.timer);
        p.reject(new Error("WebSocket closed"));
      });
      this.pendingById.clear();
      this.pendingLegacy.forEach((p) => {
        clearTimeout(p.timer);
        p.reject(new Error("WebSocket closed"));
      });
      this.pendingLegacy.clear();
      if (this.reconnectDisabled || ev.code === 4003) {
        this.reconnectDisabled = true;
        return;
      }
      this.scheduleReconnect();
    };

    socket.onerror = () => {
      // The browser closes the socket after error; the close handler will
      // take care of reconnect scheduling.
    };
  }

  private scheduleReconnect() {
    if (this.reconnectTimer != null) return;
    const delay = Math.min(
      RECONNECT_MAX_MS,
      RECONNECT_BASE_MS * 2 ** Math.min(this.reconnectAttempts, 5),
    );
    this.reconnectAttempts += 1;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, delay);
  }
}

export const socketClient = new SocketClient();
